import { z } from "zod";
import { IntegrationStatusSchema } from "../integration.js";
// Ops-admin wire contracts (ADR-0010): platform feed health, settings,
// and the cross-tenant integrations-admin catalog.
export const OperationsFeedStatus = z.enum(["healthy", "degraded", "failing", "disconnected"]);
export const OperationsFeedCategory = z.enum([
    "phone-wearable",
    "clinical",
    "platform",
    "schedule",
]);
export const OperationsFeedTile = z.object({
    id: z.string(),
    name: z.string(),
    category: OperationsFeedCategory,
    expectedCadence: z.string(),
    status: OperationsFeedStatus,
    /** Null while per-feed event accounting is not wired; never a fake zero. */
    membersLast30d: z.number().nullable(),
    eventsLast24h: z.number().nullable(),
    eventsLast7d: z.number().nullable(),
    lastEventAt: z.string().nullable(),
    reason: z.string(),
});
export const OperationsIntegrationsResponse = z.object({
    generatedAt: z.string(),
    windowDays: z.literal(30),
    tiles: z.array(OperationsFeedTile),
    summary: z.object({
        healthy: z.number(),
        degraded: z.number(),
        failing: z.number(),
        disconnected: z.number(),
    }),
});
export const OperationsTeamMember = z.object({
    userId: z.string(),
    roles: z.array(z.string()),
    capabilities: z.array(z.string()),
    isYou: z.boolean(),
});
export const OperationsRoleSummary = z.object({
    id: z.string(),
    label: z.string(),
    description: z.string(),
    capabilities: z.array(z.string()),
});
export const OperationsAuditPreviewEntry = z.object({
    id: z.string(),
    occurredAt: z.string(),
    kind: z.enum(["consent_violation", "scenario_created", "scenario_deleted"]),
    actorRef: z.string().nullable(),
    summary: z.string(),
});
export const OperationsSettingsResponse = z.object({
    generatedAt: z.string(),
    team: z.array(OperationsTeamMember),
    roles: z.array(OperationsRoleSummary),
    notificationRouting: z.array(z.object({ channel: z.string(), receives: z.string(), configured: z.boolean() })),
    /** Null until threshold policies are actually configured for the workspace. */
    thresholds: z
        .object({
        costBreachAnnualUsd: z.number(),
        urgentEventsPerDayCeiling: z.number(),
        quietHoursLocal: z.object({ start: z.string(), end: z.string() }),
        retentionDays: z.number(),
        consentRefusedFailMode: z.literal("fail_closed"),
    })
        .nullable(),
    /** Null until compliance posture facts are recorded; claims are never hardcoded. */
    workspace: z
        .object({
        baa: z.object({ status: z.enum(["covered", "not_covered"]), note: z.string() }),
        dataResidency: z.string(),
        retentionDays: z.number(),
        cloudflareScope: z.literal("cdn-only"),
    })
        .nullable(),
    audit: z.array(OperationsAuditPreviewEntry),
});
export const OperationsAdminManifestField = z.object({
    key: z.string(),
    label: z.string(),
    kind: z.enum(["text", "url", "secret"]),
    required: z.boolean(),
    help: z.string().optional(),
});
export const OperationsAdminManifest = z.object({
    slug: z.string(),
    name: z.string(),
    category: z.string(),
    version: z.number(),
    authMode: z.string(),
    capabilities: z.array(z.string()),
    channel: z.string().nullable(),
    requiresBaa: z.boolean(),
    fields: z.array(OperationsAdminManifestField),
});
export const OperationsAdminIntegrationRow = z.object({
    entitled: z.boolean(),
    status: IntegrationStatusSchema,
    config: z.record(z.string(), z.unknown()),
    secretKeysSet: z.array(z.string()),
    baaSignedAt: z.string().nullable(),
    lastHealthyAt: z.string().nullable(),
    configDrift: z.boolean(),
    live: z.boolean(),
});
export const OperationsIntegrationsAdminResponse = z.object({
    generatedAt: z.string(),
    integrations: z.array(z.object({
        manifest: OperationsAdminManifest,
        row: OperationsAdminIntegrationRow.nullable(),
    })),
});
export const OperationsIntegrationAdminCompanyParams = z.object({
    companyId: z.string().uuid(),
});
export const OperationsIntegrationAdminUpsertParams = z.object({
    companyId: z.string().uuid(),
    slug: z.string().min(1).max(80),
});
export const OperationsIntegrationAdminUpsertRequest = z.object({
    entitled: z.boolean().optional(),
    status: IntegrationStatusSchema.optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    secrets: z.record(z.string(), z.string()).optional(),
    baaSignedAt: z.string().datetime({ offset: true }).or(z.string().date()).nullable().optional(),
});
export const OperationsIntegrationAdminUpsertResponse = z.object({
    row: OperationsAdminIntegrationRow,
});
// "Log in as user" impersonation mint/redeem/revoke contracts. See
// docs/src/content/docs/adr/0034-scoped-operator-impersonation-exception.md.
// `start` is called by an authenticated operator carrying the
// admin:healthos:impersonation scope;
// `redeem` carries no bearer token at all, the one-time code IS the credential;
// `revoke` kills a live session by its id.
export const OperationsImpersonationStartRequest = z.object({
    targetUserId: z.string().uuid(),
    reason: z.string().trim().min(1),
});
export const OperationsImpersonationStartResponse = z.object({
    code: z.string(),
    expiresAt: z.string().datetime({ offset: true }),
    /** The impersonation session's id; the handle an operator uses to revoke it. */
    sessionId: z.string().uuid(),
});
export const OperationsImpersonationRedeemRequest = z.object({
    code: z.string().min(1),
});
export const OperationsImpersonationRedeemResponse = z.object({
    accessToken: z.string(),
    expiresAt: z.number().int(),
    // `name` is the target member's first name only (minimum necessary, HIPAA):
    // enough for the "Viewing as" banner to confirm the right member, never email
    // or any other identifier. Absent when the local cache has no name.
    profile: z.object({ sub: z.string(), name: z.string().optional() }),
});
export const OperationsImpersonationRevokeRequest = z.object({
    sessionId: z.string().uuid(),
});
export const OperationsImpersonationRevokeResponse = z.object({
    revoked: z.boolean(),
});
//# sourceMappingURL=operations-admin.js.map